<template>
  <div class="batch-progress-tab">
    <div class="tab-toolbar">
      <div class="summary">
        <span class="item">
          In Progress
          <strong class="warning">{{ sendingTasks.length }}</strong>
        </span>
        <span class="item">
          Pending Retry
          <strong class="danger">{{ pendingRetryTasks.length }}</strong>
        </span>
      </div>
      <el-select v-model="statusFilter" size="small" style="width: 160px" @change="onFilterChange">
        <el-option label="All Visible" value="all" />
        <el-option label="In Progress" value="sending" />
        <el-option label="Pending Retry" value="failed" />
      </el-select>
    </div>

    <div class="sending-section" v-if="pinnedSendingTasks.length">
      <div class="section-title">
        In Progress
        <span>({{ pinnedSendingTasks.length }}{{ hiddenSendingCount ? ` / ${sendingTasks.length}` : "" }})</span>
      </div>
      <div
        v-for="task in pinnedSendingTasks"
        :key="task.id"
        class="sending-row"
      >
        <div class="sending-meta">
          <el-tag size="mini" type="warning">Sending</el-tag>
          <span class="title" :title="task.title">{{ task.title }}</span>
          <span class="muted">{{ task.sent }} / {{ task.total }}</span>
        </div>
        <el-progress
          :percentage="getPercent(task)"
          :stroke-width="8"
          color="#d52b1e"
        />
      </div>
      <div class="more-sending" v-if="hiddenSendingCount > 0">
        +{{ hiddenSendingCount }} more sending tasks are in the table below
      </div>
    </div>

    <div class="table-section">
      <el-table
        :data="pagedTasks"
        size="small"
        border
        highlight-current-row
        @row-click="onRowClick"
      >
        <el-table-column prop="createdAt" label="Created" width="150" />
        <el-table-column prop="title" label="Task" min-width="220" show-overflow-tooltip />
        <el-table-column prop="pushType" label="Type" width="120" />
        <el-table-column label="Status" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusTagType(row)">
              {{ displayStatusLabel(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Progress" min-width="180">
          <template slot-scope="{ row }">
            <div class="cell-progress" @click.stop>
              <el-progress
                :percentage="getPercent(row)"
                :stroke-width="8"
                :status="row.status === 'sending' ? undefined : (row.failed > 0 ? 'exception' : 'success')"
                :color="row.status === 'sending' ? '#d52b1e' : undefined"
              />
              <span class="muted">{{ row.sent }}/{{ row.total }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Result" width="150">
          <template slot-scope="{ row }">
            <span class="success">{{ row.success }}</span>
            /
            <span class="failed" :class="{ danger: row.failed > 0 }">{{ row.failed }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Operation" width="170" align="center">
          <template slot-scope="{ row }">
            <div @click.stop>
              <template v-if="row.failed > 0">
                <el-button type="text" @click="openDrawer(row)">Failures</el-button>
                <el-button type="text" @click="openDrawer(row)">Resend</el-button>
              </template>
              <span v-else class="muted">—</span>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          small
          layout="total, prev, pager, next"
          :current-page.sync="currentPage"
          :page-size="pageSize"
          :total="filteredTasks.length"
        />
      </div>
    </div>

    <el-dialog
      title="Failed Recipients"
      :visible.sync="drawerVisible"
      width="720px"
      append-to-body
      custom-class="batch-progress-failure-dialog"
      :close-on-click-modal="false"
      @closed="onDrawerClosed"
    >
      <div v-if="activeTask">
        <div class="drawer-summary">
          <div>Task: {{ activeTask.title }}</div>
          <div>
            Failed:
            <strong>{{ activeTask.failed }}</strong>
          </div>
        </div>
        <el-table :data="failures" size="mini" border max-height="420">
          <el-table-column prop="name" label="HCP Name" min-width="90" />
          <el-table-column prop="hcpId" label="HCP ID" min-width="100" />
          <el-table-column prop="hospital" label="Hospital" min-width="140" show-overflow-tooltip />
          <el-table-column prop="reason" label="Reason" min-width="130" show-overflow-tooltip />
        </el-table>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeDrawer">Close</el-button>
        <el-button type="primary" :loading="resending" @click="onResend">
          One-click Resend
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "BatchProgressTab",
  data() {
    return {
      tasks: [],
      failures: [],
      statusFilter: "all",
      currentPage: 1,
      pageSize: 8,
      pinLimit: 5,
      drawerVisible: false,
      activeTaskId: "",
      resending: false,
      suppressRowClick: false,
      pollTimer: null,
    };
  },
  created() {
    this.loadTasks();
    this.pollTimer = setInterval(() => this.loadTasks(), 5000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  computed: {
    // Only in-progress and failed-pending-retry; fully successful tasks stay off this page
    visibleTasks() {
      return this.tasks.filter(
        (item) => item.status === "sending" || item.failed > 0
      );
    },
    sendingTasks() {
      return this.visibleTasks.filter((item) => item.status === "sending");
    },
    pendingRetryTasks() {
      return this.visibleTasks.filter(
        (item) => item.status !== "sending" && item.failed > 0
      );
    },
    pinnedSendingTasks() {
      return this.sendingTasks.slice(0, this.pinLimit);
    },
    hiddenSendingCount() {
      return Math.max(0, this.sendingTasks.length - this.pinLimit);
    },
    filteredTasks() {
      if (this.statusFilter === "sending") {
        return this.sendingTasks;
      }
      if (this.statusFilter === "failed") {
        return this.pendingRetryTasks;
      }
      return this.visibleTasks;
    },
    pagedTasks() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredTasks.slice(start, start + this.pageSize);
    },
    activeTask() {
      return this.tasks.find((item) => item.id === this.activeTaskId) || null;
    },
  },
  methods: {
    getPercent(task) {
      if (!task || !task.total) return 0;
      return Math.min(100, Math.round((task.sent / task.total) * 100));
    },
    loadTasks() {
      this.axios.get("/batchPush/tasks").then(({ code, data }) => {
        if (code === 200) {
          this.tasks = data || [];
          this.$emit("summary", {
            sending: this.tasks.filter((item) => item.status === "sending").length,
          });
        }
      }).catch(() => {});
    },
    displayStatusLabel(row) {
      if (row.status === "sending") return "In Progress";
      if (row.failed > 0) return "Pending Retry";
      return row.statusLabel || "Done";
    },
    statusTagType(row) {
      if (row.status === "sending") return "warning";
      if (row.failed > 0) return "danger";
      return "success";
    },
    onFilterChange() {
      this.currentPage = 1;
    },
    onRowClick(row) {
      if (this.suppressRowClick) return;
      if (row.failed > 0) {
        this.openDrawer(row);
      }
    },
    openDrawer(task) {
      this.activeTaskId = task.id;
      this.failures = [];
      this.drawerVisible = true;
      this.axios.get("/batchPush/failures", { params: { taskId: task.id } }).then(({ code, data }) => {
        if (code === 200) this.failures = data || [];
      }).catch(() => {});
    },
    closeDrawer() {
      this.suppressRowClick = true;
      this.drawerVisible = false;
      setTimeout(() => {
        this.suppressRowClick = false;
      }, 400);
    },
    onDrawerClosed() {
      this.resending = false;
      this.activeTaskId = "";
    },
    onResend() {
      if (!this.activeTask || this.activeTask.failed <= 0) return;
      this.resending = true;
      this.axios.post("/batchPush/resend", { taskId: this.activeTask.id }).then(({ code, msg }) => {
        this.resending = false;
        if (code === 200) {
          this.$message.success(msg || "Resend started.");
          this.closeDrawer();
          this.loadTasks();
        } else {
          this.$message.error(msg || "Resend failed.");
        }
      }).catch(() => {
        this.resending = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.batch-progress-tab {
  padding: 0 16px 16px;
}

.tab-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  color: #606266;

  strong {
    margin-left: 4px;
    color: #303133;
  }

  .warning {
    color: #e6a23c;
  }

  .danger {
    color: #d52b1e;
  }

  .mock-flag {
    padding: 2px 8px;
    border-radius: 10px;
    background: #fff1f0;
    color: #d52b1e;
    font-size: 12px;
  }
}

.section-title {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;

  span {
    margin-left: 6px;
    color: #909399;
    font-weight: 400;
  }
}

.sending-section {
  margin-bottom: 12px;
  padding: 12px 14px;
  border: 1px solid #f5dab1;
  border-radius: 6px;
  background: #fffaf3;
}

.sending-row {
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.sending-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  min-width: 0;

  .title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: #303133;
    font-weight: 600;
  }
}

.more-sending {
  margin-top: 8px;
  font-size: 12px;
  color: #e6a23c;
}

.table-section {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px;
}

.cell-progress {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
}

.success {
  color: #67c23a;
}

.failed {
  color: #606266;

  &.danger {
    color: #d52b1e;
    font-weight: 600;
  }
}

.muted {
  color: #909399;
  font-size: 12px;
}

.pager {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.drawer-summary {
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
  line-height: 22px;

  strong {
    color: #d52b1e;
  }
}

.dialog-footer {
  text-align: right;
}
</style>
